import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { checkIsAdmin, checkIsGlobalAdmin } from '../../utils/authUtils';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSearch,
  FaArrowLeft,
  FaUsersCog,
  FaExclamationTriangle,
  FaCheckCircle,
  FaSave,
  FaTimes,
  FaExclamationCircle,
  FaCog,
  FaBuilding,
  FaMapMarkerAlt,
  FaUserCheck
} from 'react-icons/fa';
import CustomerCustomizationModal from "./CustomerCustomizationModal";
import { UserContext } from '../../Context/UserContext';
import { toast } from "react-toastify";
import "./CreateCustomerPage.css";

/**
 * EmployeeType Page
 * Manages employee classifications with dynamic metadata support.
 */
const EmployeeType = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { baseUrl, configLoading, getHeaders } = useContext(UserContext);

  // Data States
  const [types, setTypes] = useState([]);
  const [doctypeFields, setDoctypeFields] = useState([]);
  const [permissions, setPermissions] = useState({ canWrite: false, canDelete: false });

  // UI States
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [showCustomizeModal, setShowCustomizeModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [warningMessage, setWarningMessage] = useState('');
  const [warningType, setWarningType] = useState('success');
  // Form & Multi-Tenancy States
  const [formData, setFormData] = useState({});
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);


  // Filter state for dynamic branches
  const [filterBranches, setFilterBranches] = useState([]);
  const [selectedViewCompany, setSelectedViewCompany] = useState('');

  useEffect(() => {
    const fetchFilterBranches = async () => {
      try {
        const url = baseUrl || "";
        const comp = selectedViewCompany || localStorage.getItem('active_company');
        if (!comp || comp === 'All') {
          setFilterBranches(availableBranches); // fallback
          return;
        }

        const localGetHeaders = () => {
          const token = localStorage.getItem('token');
          return token ? { Authorization: `Bearer ${token}` } : {};
        };
        const headersFunc = typeof getHeaders === 'function' ? getHeaders : localGetHeaders;

        const resp = await axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, { headers: { ...headersFunc(), "X-Company-Name": comp } });
        setFilterBranches([...new Set(resp.data.map(b => typeof b === 'string' ? b : (b.branch_name || b.name)).filter(Boolean))]);
      } catch (err) {
        console.error("Error fetching filter branches:", err);
      }
    };
    fetchFilterBranches();
  }, [selectedViewCompany, baseUrl, availableBranches]);



  const [selectedBranchFilter, setSelectedBranchFilter] = useState('');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [companyBranchesMap, setCompanyBranchesMap] = useState({});
  const [selectedAddBranches, setSelectedAddBranches] = useState([]);

  // Fetch DocType Metadata
  const fetchMetadata = async (url) => {
    try {
      const response = await axios.get(`${url}/api/doctypes/${encodeURIComponent('Employee Type')}`, { headers: getHeaders() });
      const fields = response.data.fields || [];
      setDoctypeFields(fields);

      // Initialize form data with default values from metadata
      const initialForm = { branch_names: [] };
      fields.forEach(f => {
        const type = (f.type || '').toLowerCase();
        if (type === 'check') initialForm[f.id] = f.default_value !== undefined ? f.default_value : false;
        else initialForm[f.id] = f.default_value || '';
      });
      setFormData(initialForm);
    } catch (err) {
      console.error("Metadata Fetch Error:", err);
      // Fallback to basic fields if API fails
      const fallbackFields = [
        { id: 'type_name', label: 'Department / Type', type: 'Data', mandatory: true },
        { id: 'description', label: 'Description', type: 'Text', mandatory: true },
        { id: 'salary_range', label: 'Salary Range', type: 'Data', mandatory: true }
      ];
      setDoctypeFields(fallbackFields);
      const initialForm = { branch_names: [] };
      fallbackFields.forEach(f => initialForm[f.id] = '');
      setFormData(initialForm);
    }
  };

  // Fetch All Types
  const fetchTypesData = async (url) => {
    try {
      const response = await axios.get(`${url}/api/employee-types`, { headers: getHeaders() });
      setTypes(response.data);
    } catch (err) {
      console.error("Fetch Types Error:", err);
      toast.error("Failed to load employee types.");
    }
  };

  // Fetch Permissions & Context
  const fetchContext = async (url) => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) return;
      const user = JSON.parse(userStr);
      const roleRaw = user.role?.toLowerCase() || user.UserType?.toLowerCase() || '';
      const role = roleRaw.replace(' ', '_');
      const branch = user.branch_name || user.branch || "";

      const is_Admin = ['company_admin', 'admin', 'group_admin', 'groupadmin', 'tenant_admin', 'super_admin'].includes(role) && (!branch || branch === 'All Branches' || branch === "");
      const is_Group = ['group_admin', 'groupadmin', 'tenant_admin', 'super_admin'].includes(role);

      setIsGroupAdmin(is_Group);
      setIsCompanyAdmin(is_Admin);

      // Admin bypass for permissions
      if (is_Admin || role === 'branch_admin') {
        setPermissions({ canWrite: true, canDelete: true });
      } else {
        const resp = await axios.get(`${url}/api/role-permissions?role=${encodeURIComponent(roleRaw)}`, { headers: getHeaders() });
        const perms = resp.data.permissions || [];
        const pagePerm = perms.find(p => p.pageId === 'employee_types');
        setPermissions({
          canWrite: pagePerm?.canWrite || false,
          canDelete: pagePerm?.canDelete || false
        });
      }

      // Handle Multi-Tenancy Selection
      let comps = [];
      if (user.companies && user.companies.length > 0) {
        comps = user.companies.map(c => typeof c === 'object' && c !== null ? (c.company_name || c.company || c.name || '') : c).filter(c => c && typeof c === 'string');
      } else if (user.company_name || user.company) {
        comps = [user.company_name || user.company];
      } else {
        const res = await axios.get(`${url}/api/company-details`, { headers: typeof getHeaders === 'function' ? getHeaders() : {} });
        const details = res.data.companyDetails || [];
        comps = details.map(d => d.restaurantName || d.company_name).filter(n => n);
      }
      const finalComps = comps.length > 0 ? [...new Set(comps)] : [];
      setCompanyOptions(finalComps);

      const activeContext = localStorage.getItem('active_company');
      if (activeContext && activeContext !== 'All') {
        setSelectedCompanies([activeContext || user.company_name || ""]);
      } else if (activeContext === 'All' || !activeContext) {
        setSelectedCompanies([]); // Let user select in the modal
      }

      // Fetch Branches per company (SaaS: must pass company_name)
      const allBranchNames = [];
      await Promise.all(finalComps.map(async comp => {
        try {
          const res = await axios.get(
            `${url}/api/branches?company_name=${encodeURIComponent(comp)}`,
            { headers: { ...getHeaders(), 'X-Company-Name': comp } }
          );
          (res.data || []).forEach(b => {
            const n = typeof b === 'string' ? b : (b.branch_name || b.name || '');
            if (n && !allBranchNames.includes(n)) allBranchNames.push(n);
          });
        } catch (e) { console.warn('Branch fetch error', comp, e); }
      }));
      setAvailableBranches(allBranchNames);

    } catch (err) {
      console.error("Context Fetch Error:", err);
    }
  };

  useEffect(() => {
    if (configLoading) return;
    const url = baseUrl || "";

    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchMetadata(url),
        fetchTypesData(url),
        fetchContext(url)
      ]);
      setLoading(false);
    };
    init();
  }, [baseUrl, configLoading]);

  // Handle Navigation State
  useEffect(() => {
    if (location.state?.fromAddEmployee) {
      setShowForm(true);
      if (location.state.selectedBranches) {
        setFormData(prev => ({ ...prev, branch_names: location.state.selectedBranches }));
      }
    }
  }, [location.state]);

  const handleBack = () => {
    if (location.state?.fromAddEmployee) {
      navigate('/add-employee', { state: location.state?.originalAddEmployeeState || location.state });
    } else if (showForm) {
      setShowForm(false);
      setEditingId(null);
      setFormData({});
    } else {
      navigate('/admin');
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const toggleBranchSelection = (branchName) => {
    setFormData(prev => {
      const current = prev.branch_names || [];
      return {
        ...prev,
        branch_names: current.includes(branchName)
          ? current.filter(b => b !== branchName)
          : [...current, branchName]
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!permissions.canWrite) return toast.error("Unauthorized access.");

    // Validation
    const missing = doctypeFields.filter(f => f.mandatory && !formData[f.id]);
    if (missing.length > 0) return toast.error(`Missing required fields: ${missing.map(f => f.label).join(', ')}`);

    try {
      setLoading(true);
      const url = `${baseUrl || ""}/api/employee-types`;
      const activeContext = localStorage.getItem('active_company');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const finalCompanies = activeContext === 'All' ? selectedCompanies : [activeContext || user.company_name || ""];

      const activeBranchLocal = (localStorage.getItem('active_branch') || 'All Branches').trim();
      const isSpecificBranchActive = activeBranchLocal.toLowerCase() !== 'all branches' && activeBranchLocal.toLowerCase() !== 'all' && activeBranchLocal !== '';
      const branchesToProcess = isSpecificBranchActive ? [activeBranchLocal] : (formData.branch_names || []);
      const payload = {
        ...formData,
        company_names: finalCompanies,
        company_name: finalCompanies[0] || "",
        branch_names: branchesToProcess,
        branch_name: branchesToProcess[0] || ""
      };

      if (editingId) {
        const itemToEdit = types.find(t => (t._id || t.id) === editingId);
        if (itemToEdit?.global_ref_id) {
          await axios.put(`${url}/bulk/${itemToEdit.global_ref_id}`, payload, { headers: getHeaders() });
        } else {
          await axios.put(`${url}/${editingId}`, payload, { headers: getHeaders() });
        }
        setWarningMessage("Type updated successfully.");
      } else {
        await axios.post(url, payload, { headers: getHeaders() });
        setWarningMessage("Type added successfully.");
      }
      setWarningType("success");
    } catch (err) {
      setWarningMessage(err.response?.data?.error || "Failed to save type.");
      setWarningType("error");
    } finally {
      setLoading(false);
    }
  };

  const initiateEdit = (type) => {
    let relatedTypes = [type];
    if (type.global_ref_id) {
      relatedTypes = types.filter(t => t.global_ref_id === type.global_ref_id);
    }

    setEditingId(type._id || type.id);

    const comps = new Set();
    const brs = new Set();

    relatedTypes.forEach(t => {
      if (t.company_names && Array.isArray(t.company_names)) t.company_names.forEach(c => comps.add(c));
      else if (t.company_name) comps.add(t.company_name);

      if (t.branch_names && Array.isArray(t.branch_names)) t.branch_names.forEach(b => brs.add(b));
      else if (t.branch_names && typeof t.branch_names === 'string') t.branch_names.split(',').forEach(b => brs.add(b.trim()));
      else if (t.branch_name) brs.add(t.branch_name);
    });

    setSelectedCompanies(Array.from(comps));
    const finalBranches = Array.from(brs);

    setFormData({
      ...type,
      branch_names: finalBranches
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };


  // Dynamic form branches based on selected companies
  const [formBranches, setFormBranches] = useState([]);
  useEffect(() => {
    const fetchFormBranches = async () => {
      try {
        const url = baseUrl || "";
        const compsToFetch = selectedCompanies.length > 0 ? selectedCompanies : companyOptions;
        if (!compsToFetch || compsToFetch.length === 0) {
          setFormBranches(availableBranches);
          return;
        }

        const localGetHeaders = () => {
          const token = localStorage.getItem('token');
          return token ? { Authorization: `Bearer ${token}` } : {};
        };
        const headersFunc = typeof getHeaders === 'function' ? getHeaders : localGetHeaders;

        const branchPromises = compsToFetch.map(comp =>
          axios.get(`${url}/api/branches?company_name=${encodeURIComponent(comp)}`, {
            headers: { ...headersFunc(), "X-Company-Name": comp }
          }).then(res => res.data)
        );

        const results = await Promise.all(branchPromises);
        const allBranches = [];
        results.forEach(data => {
          const branches = data.map(b => typeof b === 'string' ? b : (b.branch_name || b.branch || b.name)).filter(Boolean);
          allBranches.push(...branches);
        });

        setFormBranches([...new Set(allBranches)]);
      } catch (err) {
        console.error("Error fetching form branches:", err);
      }
    };
    fetchFormBranches();
  }, [selectedCompanies, companyOptions, baseUrl, availableBranches]);

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setShowDeleteConfirm(true);
  };

  const executeDelete = async () => {
    if (!itemToDelete) return;
    try {
      const itemToDel = types.find(t => (t._id || t.id) === itemToDelete);
      const url = baseUrl || "";
      const endpoint = itemToDel?.global_ref_id
        ? `${url}/api/employee-types/bulk/${itemToDel.global_ref_id}`
        : `${url}/api/employee-types/${itemToDelete}`;
      await axios.delete(endpoint, { headers: getHeaders() });
      toast.warn("Type deleted.");
      fetchTypesData(baseUrl || "");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete type.");
    } finally {
      setShowDeleteConfirm(false);
      setItemToDelete(null);
    }
  };

  const filteredTypes = types.filter(t => {
    const query = searchQuery.toLowerCase();
    const matchesSearch = !query ||
      (t.name || '').toLowerCase().includes(query) ||
      (t.description || '').toLowerCase().includes(query);

    let matchesCompany = true;
    if (selectedViewCompany) {
      const tComps = t.company_names || (t.company_name ? [t.company_name] : []);
      matchesCompany = tComps.includes(selectedViewCompany);
    }

    let matchesBranch = true;
    if (selectedBranchFilter) {
      const tBranches = t.branch_names || (t.branch_name ? [t.branch_name] : []);
      matchesBranch = tBranches.includes(selectedBranchFilter);
    }

    return matchesSearch && matchesCompany && matchesBranch;
  });

  if (loading && types.length === 0) return <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div>;

  return (
    <div className="create-customer-container main-customer-page">
      {/* Header */}
      <div className="customer-header-section">
        <div className="header-left">
          <button onClick={handleBack} className="header-back-btn">
            <FaArrowLeft /> Back
          </button>
        </div>
        <div className="header-center">
          <h1><FaUsersCog style={{marginRight: '12px', color: '#604BE8'}} /> Employee Types</h1>
        </div>
        <div className="header-right">
          <div className="header-actions">
            {(isGroupAdmin || isCompanyAdmin) && (
              <button onClick={() => setShowCustomizeModal(true)} className="tab-btn" style={{ background: '#3b82f6' }}>
                <FaCog /> Customize
              </button>
            )}
            {!showForm && permissions.canWrite && (
              <button onClick={() => setShowForm(true)} className="add-btn">
                <FaPlus /> Add New
              </button>
            )}
            {showForm && (
              <>
                <button type="button" className="danger-btn" onClick={handleBack} style={{ background: '#f1f5f9', color: '#64748b', boxShadow: 'none' }}>Cancel</button>
                <button type="submit" form="type-form" disabled={loading} className="save-btn">
                  <FaSave /> {loading ? 'Processing...' : (editingId ? 'Update' : 'Save')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="customer-scroll-body">
        <div className="form-sections-wrapper" style={{ maxWidth: '1200px', width: '100%' }}>
          {showForm ? (
            <form id="type-form" onSubmit={handleSubmit} className="form-section-card theme-blue">
              <div className="section-header">
                <h2>{editingId ? "Edit Type :" : "New Type :"}</h2>
              </div>
              <div className="form-grid" style={{ marginBottom: '30px' }}>
                {doctypeFields.filter(f => !f.hidden).map(field => {
                  const type = (field.type || '').toLowerCase();
                  const isDisabled = field.disabled || field.read_only;

                  return (
                    <div key={field.id} className="form-group">
                      {type === 'check' ? (
                        <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: isDisabled ? 'not-allowed' : 'pointer', marginTop: '28px', background: '#F3F6FB', padding: '12px 16px', borderRadius: '8px', border: '1px solid transparent' }}>
                          <input type="checkbox" name={field.id} checked={!!formData[field.id]} onChange={handleInputChange} disabled={isDisabled} style={{ width: '18px', height: '18px', margin: 0, accentColor: '#604BE8' }} />
                          <span style={{ fontWeight: '600', color: '#1B1B29' }}>{field.label}</span>
                        </label>
                      ) : (
                        <>
                          <label>
                            {field.label} {field.mandatory && <span className="required">*</span>}
                          </label>
                          {type === 'text' ? (
                            <textarea name={field.id} value={formData[field.id] || ''} onChange={handleInputChange} disabled={isDisabled} style={{ width: '100%', minHeight: '100px', padding: '12px 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none', resize: 'vertical' }} placeholder={`Enter ${field.label}`} />
                          ) : (
                            <input type={type === 'number' ? 'number' : 'text'} name={field.id} value={formData[field.id] || ''} onChange={handleInputChange} disabled={isDisabled} placeholder={`Enter ${field.label}`} />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Multi-Tenancy UI */}
              <div style={{ background: '#f8fafc', padding: '25px', borderRadius: '15px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
                {companyOptions.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '15px', fontWeight: '700', color: '#1B1B29' }}>Assign to Companies *</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {companyOptions.map((comp, idx) => (
                        <label key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: '#eff2f9', borderRadius: '12px', border: '1.5px solid #C3CDE4', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                          <input
                            type="checkbox"
                            checked={selectedCompanies.includes(comp)}
                            onChange={() => {
                              setSelectedCompanies(prev =>
                                prev.includes(comp) ? prev.filter(c => c !== comp) : [...prev, comp]
                              );
                            }}
                            style={{ width: '18px', height: '18px', accentColor: '#604BE8' }}
                          />
                          {comp}
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {(isGroupAdmin || (isCompanyAdmin && !localStorage.getItem('branch_name'))) && (
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '15px', fontWeight: '700', color: '#1B1B29' }}>Assign to Branches</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                      {(formBranches && formBranches.length > 0 ? formBranches : availableBranches).map(br => {
                        const isSelected = formData.branch_names?.includes(br);
                        return (
                          <label key={br} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 18px', background: isSelected ? '#ecfdf5' : '#eff2f9', borderRadius: '12px', border: `1.5px solid ${isSelected ? '#d1fae5' : '#C3CDE4'}`, cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
                            <input type="checkbox" checked={isSelected} onChange={() => toggleBranchSelection(br)} style={{ width: '18px', height: '18px', accentColor: '#06D6A0' }} />
                            {br}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Submit Buttons Moved to Header */}
            </form>
          ) : (
            <div className="form-section-card theme-blue">
              <div className="section-header" style={{ marginBottom: '20px' }}>
                <h2>Types List</h2>
              </div>
              {/* Filter Bar */}
              <div style={{ display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap', alignItems: 'center' }}>
                <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
                  <FaSearch style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                  <input type="text" placeholder="Search by name or description..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} style={{ width: '100%', height: '44px', padding: '0 16px 0 45px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none', transition: 'all 0.3s ease', boxSizing: 'border-box' }} onFocus={(e) => e.target.style.borderColor = '#604BE8'} onBlur={(e) => e.target.style.borderColor = 'transparent'} />
                </div>

                {companyOptions.length > 1 && (
                  <div style={{ width: '200px' }}>
                    <select
                      value={selectedViewCompany}
                      onChange={(e) => setSelectedViewCompany(e.target.value)}
                      style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none' }}
                    >
                      <option value="">All Companies</option>
                      {companyOptions.map(comp => <option key={comp} value={comp}>{comp}</option>)}
                    </select>
                  </div>
                )}

                {(!localStorage.getItem('active_branch') || localStorage.getItem('active_branch') === 'All' || localStorage.getItem('active_branch') === 'All Branches') && (
                  <div style={{ width: '200px' }}>
                    <select
                      value={selectedBranchFilter}
                      onChange={(e) => setSelectedBranchFilter(e.target.value)}
                      style={{ width: '100%', height: '44px', padding: '0 16px', background: '#F3F6FB', border: '1px solid transparent', borderRadius: '8px', fontSize: '14px', fontWeight: '600', color: '#1B1B29', outline: 'none' }}
                    >
                      <option value="">All Branches</option>
                      {(filterBranches.length > 0 ? filterBranches : availableBranches).map(br => <option key={br} value={br}>{br}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {/* Data Table */}
              <div style={{ width: '100%', backgroundColor: '#ffffff', border: 'none' }} className="premium-card">
                <table className="premium-table">
                  <thead>
                    <tr>
                      {doctypeFields.filter(f => !f.hidden).slice(0, 4).map(f => (
                        <th key={f.id}>{f.label}</th>
                      ))}
                      <th>Companies</th>
                      <th>Branches</th>
                      <th style={{ textAlign: 'right', paddingRight: '36px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTypes.map(t => (
                      <tr key={t._id || t.id}>
                        {doctypeFields.filter(f => !f.hidden).slice(0, 4).map(f => (
                          <td key={f.id} style={{ fontWeight: f.id === 'name' ? '700' : '500', color: f.id === 'name' ? '#1B1B29' : '#4A4A5A', fontSize: f.id === 'name' ? '15px' : '14px' }}>
                            {t[f.id] || '-'}
                          </td>
                        ))}
                        <td>
                          <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                            {(t.company_names || (t.company_name ? [t.company_name] : [])).join(', ') || '-'}
                          </span>
                        </td>
                        <td>
                          <span style={{ padding: '4px 12px', borderRadius: '6px', background: '#eff2f9', color: '#604BE8', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase' }}>
                            {(t.branch_names || (t.branch_name ? [t.branch_name] : [])).join(', ') || '-'}
                          </span>
                        </td>
                        <td style={{ paddingRight: '24px' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                            <button onClick={() => initiateEdit(t)} className="action-btn action-btn-edit" title="Edit">
                              <FaEdit style={{ fontSize: '20px' }} />
                            </button>
                            <button onClick={() => handleDeleteClick(t._id || t.id)} className="action-btn action-btn-delete" title="Delete">
                              <FaTrash style={{ fontSize: '20px' }} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredTypes.length === 0 && (
                  <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '15px', fontWeight: '600' }}>
                    No types found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Delete Confirm Modal ── */}
      {showDeleteConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100,
          padding: '20px'
        }}>
          <div style={{
            background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '400px',
            padding: '32px', textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
            animation: 'slideUp 0.3s ease-out'
          }}>
            <div style={{
              width: '72px', height: '72px', borderRadius: '50%', background: '#fff0f0',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto'
            }}>
              <FaTrash style={{ fontSize: '28px', color: '#FF595E' }} />
            </div>
            <h3 style={{ color: '#1B1B29', fontSize: '22px', fontWeight: '700', margin: '0 0 10px 0' }}>Remove Type?</h3>
            <p style={{ color: '#777777', fontSize: '15px', marginBottom: '30px', lineHeight: '1.6', fontWeight: '500' }}>
              Are you sure you want to delete this employee type? This action is permanent and cannot be reversed.
            </p>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: '1px solid #E2E6EF', background: '#ffffff', color: '#1B1B29', fontWeight: '600', fontSize: '14px', cursor: 'pointer' }}>
                Keep Type
              </button>
              <button onClick={executeDelete} style={{ flex: 1, padding: '14px', borderRadius: '10px', border: 'none', background: '#FF595E', color: '#ffffff', fontWeight: '600', fontSize: '14px', cursor: 'pointer', boxShadow: '0 4px 15px rgba(255, 89, 94, 0.3)' }}>
                Delete Forever
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Warning / Success Modal */}
      {warningMessage && (
        <div className="premium-modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999,
          animation: 'fadeIn 0.3s ease-out'
        }}>
          <div className="premium-modal-content" style={{
            background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '420px', padding: '40px 32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', position: 'relative', overflow: 'hidden',
            animation: 'slideUpFade 0.4s cubic-bezier(0.16, 1, 0.3, 1)'
          }}>
            <div style={{
              position: 'absolute', top: '-50px', left: '50%', transform: 'translateX(-50%)',
              width: '200px', height: '200px', zIndex: 0, pointerEvents: 'none',
              background: warningType === 'success' ? 'radial-gradient(circle, rgba(6, 214, 160, 0.15) 0%, rgba(255,255,255,0) 70%)' : 'radial-gradient(circle, rgba(255, 159, 4, 0.15) 0%, rgba(255,255,255,0) 70%)'
            }}></div>
            <div className="premium-modal-icon" style={{
              width: '84px', height: '84px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '38px', marginBottom: '24px', zIndex: 1, animation: 'bounceIn 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
              background: warningType === 'success' ? '#ecfdf5' : '#fffbeb',
              color: warningType === 'success' ? '#06D6A0' : '#FF9F04',
              border: `2px solid ${warningType === 'success' ? '#a7f3d0' : '#fde68a'}`,
              boxShadow: warningType === 'success' ? '0 10px 25px -5px rgba(6, 214, 160, 0.2)' : '0 10px 25px -5px rgba(255, 159, 4, 0.2)'
            }}>
              {warningType === 'success' ? <FaUserCheck /> : <FaExclamationTriangle />}
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: '800', color: '#1e293b', margin: '0 0 12px 0', zIndex: 1, fontFamily: `'Gilroy', 'Inter', sans-serif` }}>
              {warningType === 'success' ? "Success" : "Notice"}
            </h3>
            <p style={{ fontSize: '16px', color: '#64748b', lineHeight: '1.6', margin: '0 0 32px 0', zIndex: 1, fontWeight: '500' }}>
              {warningMessage}
            </p>
            <div className="premium-modal-actions" style={{ display: 'flex', gap: '16px', width: '100%', zIndex: 1 }}>
              {warningType !== 'success' && (
                <button type="button" className="premium-btn-cancel" onClick={() => setWarningMessage("")}
                  style={{
                    flex: 1, height: '48px', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: '14px',
                    fontWeight: '700', fontSize: '15px', cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: `'Gilroy', 'Inter', sans-serif`
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}>
                  Cancel
                </button>
              )}
              <button type="button" className="premium-btn-ok"
                onClick={() => {
                  setWarningMessage("");
                  if (warningType === 'success') {
                    if (location.state?.fromAddEmployee) {
                      navigate('/add-employee', {
                        state: {
                          formData: { ...location.state.formData, employeeType: formData.name },
                          newEmployeeType: formData.name
                        }
                      });
                    } else {
                      setShowForm(false);
                      setEditingId(null);
                      fetchTypesData(baseUrl || "");
                    }
                  }
                }}
                style={{
                  flex: 1, height: '48px', color: '#ffffff', border: 'none', borderRadius: '14px', fontWeight: '700', fontSize: '15px',
                  cursor: 'pointer', transition: 'all 0.2s ease', fontFamily: `'Gilroy', 'Inter', sans-serif`,
                  background: warningType === 'success' ? '#06D6A0' : '#604BE8',
                  boxShadow: warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 12px 25px -5px rgba(6, 214, 160, 0.4)' : '0 12px 25px -5px rgba(96, 75, 232, 0.4)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = warningType === 'success' ? '0 10px 20px -5px rgba(6, 214, 160, 0.3)' : '0 10px 20px -5px rgba(96, 75, 232, 0.3)'; }}>
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      <CustomerCustomizationModal
        isOpen={showCustomizeModal}
        onClose={() => setShowCustomizeModal(false)}
        onRefresh={() => fetchMetadata(baseUrl || "")}
        targetDocType="Employee Type"
      />
    </div>
  );
};

export default EmployeeType;